import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  clearStoredAuthIfApiError,
  DIGIRASA_AUTH_UPDATED_EVENT,
  extractApiErrorMessage,
  readStoredAuth,
  SIGN_IN_REQUIRED_MESSAGE,
} from '../Account/profileFormUtils';
import { findCouponByCode, isCouponEligible } from './cartCouponUtils';
import {
  clearStoredAppliedCoupon,
  persistAppliedCoupon,
  readStoredAppliedCoupon,
} from './cartCouponStorage';
import { fetchActiveCoupons } from './couponsApi';

/**
 * @param {{ cartTotal: number }} options
 */
export function useCartCoupons({ cartTotal }) {
  const [coupons, setCoupons] = useState(/** @type {import('./cartCouponUtils').NormalizedCoupon[]} */ ([]));
  const [appliedCoupon, setAppliedCoupon] = useState(() => readStoredAppliedCoupon());
  const [status, setStatus] = useState(/** @type {'guest' | 'loading' | 'loaded' | 'error'} */ ('guest'));
  const [error, setError] = useState('');
  const [codeInput, setCodeInput] = useState(() => readStoredAppliedCoupon()?.code ?? '');
  const [codeError, setCodeError] = useState('');

  const dropAppliedCoupon = useCallback(() => {
    setAppliedCoupon(null);
    clearStoredAppliedCoupon();
  }, []);

  const loadCoupons = useCallback(async () => {
    const { token } = readStoredAuth();
    if (!token) {
      setStatus('guest');
      setCoupons([]);
      dropAppliedCoupon();
      setError('');
      return;
    }

    setStatus('loading');
    setError('');
    try {
      const { coupons: list } = await fetchActiveCoupons(token);
      setCoupons(list);
      setStatus('loaded');
    } catch (err) {
      clearStoredAuthIfApiError(err);
      const { token: tokenAfter } = readStoredAuth();
      if (!tokenAfter) {
        setStatus('guest');
        setCoupons([]);
        dropAppliedCoupon();
        setError('');
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        setStatus('loading');
        setError('');
        return;
      }
      setCoupons([]);
      setStatus('error');
      const details = err instanceof ApiError ? err.details : null;
      setError(
        extractApiErrorMessage(details) ||
          (err instanceof Error ? err.message : 'Could not load coupons. Please try again.'),
      );
    }
  }, [dropAppliedCoupon]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  useEffect(() => {
    const onAuth = () => {
      loadCoupons();
    };
    window.addEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
    return () => window.removeEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
  }, [loadCoupons]);

  // A coupon restored from a previous screen may have expired or been withdrawn,
  // so re-resolve it against the freshly loaded list before it can be charged.
  useEffect(() => {
    if (status !== 'loaded' || !appliedCoupon) return;
    const fresh = findCouponByCode(coupons, appliedCoupon.code);
    if (!fresh) {
      dropAppliedCoupon();
      setCodeInput('');
    } else if (fresh !== appliedCoupon) {
      setAppliedCoupon(fresh);
      persistAppliedCoupon(fresh);
    }
  }, [status, coupons, appliedCoupon, dropAppliedCoupon]);

  useEffect(() => {
    if (appliedCoupon && !isCouponEligible(appliedCoupon, cartTotal)) {
      dropAppliedCoupon();
      setCodeError('');
    }
  }, [appliedCoupon, cartTotal, dropAppliedCoupon]);

  const applyCoupon = useCallback(
    /** @param {import('./cartCouponUtils').NormalizedCoupon} coupon */
    (coupon) => {
      if (!isCouponEligible(coupon, cartTotal)) {
        setCodeError(
          `Minimum order of ₹${(coupon.minOrderValue ?? 0).toFixed(0)} required for this coupon.`,
        );
        return false;
      }
      setAppliedCoupon(coupon);
      persistAppliedCoupon(coupon);
      setCodeInput(coupon.code);
      setCodeError('');
      return true;
    },
    [cartTotal],
  );

  const applyCouponByCode = useCallback(
    /** @param {string} code */
    (code) => {
      const match = findCouponByCode(coupons, code);
      if (!match) {
        setCodeError('Invalid coupon code. Choose from available coupons or check the code.');
        return false;
      }
      return applyCoupon(match);
    },
    [applyCoupon, coupons],
  );

  const removeCoupon = useCallback(() => {
    dropAppliedCoupon();
    setCodeInput('');
    setCodeError('');
  }, [dropAppliedCoupon]);

  const handleApplyCodeInput = useCallback(() => {
    if (status === 'guest') {
      setCodeError(SIGN_IN_REQUIRED_MESSAGE);
      return false;
    }
    if (status === 'loading') return false;
    return applyCouponByCode(codeInput);
  }, [applyCouponByCode, codeInput, status]);

  return {
    coupons,
    appliedCoupon,
    status,
    error,
    codeInput,
    setCodeInput,
    codeError,
    setCodeError,
    loadCoupons,
    applyCoupon,
    applyCouponByCode,
    removeCoupon,
    handleApplyCodeInput,
  };
}
